import { useState } from "react";
import { Languages, Menu, PhoneCall, X } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { companyConfig, getSiteContent } from "@/data/siteContent";

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { language, toggleLanguage, isArabic } = useLanguage();
  const content = getSiteContent(language);

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/95 backdrop-blur-md" data-testid="main-navbar">
      <div className="container mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo-link">
          <img
            src={companyConfig.logoUrl}
            alt={companyConfig.nameEn}
            className="h-12 w-12 rounded-lg border border-slate-200 object-contain bg-white p-1"
            data-testid="navbar-logo-image"
          />
          <div>
            <p className="text-sm font-semibold text-slate-900" data-testid="navbar-company-name-ar">
              {companyConfig.nameAr}
            </p>
            <p className="text-xs text-slate-500" data-testid="navbar-company-name-en">
              {companyConfig.nameEn}
            </p>
          </div>
        </Link>

        <nav className="hidden items-center gap-5 lg:flex" data-testid="desktop-navigation-menu">
          {content.nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.to === "/" ? "home" : item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `text-sm font-semibold transition-colors duration-300 hover:text-secondary ${
                  isActive ? "text-secondary" : "text-slate-700"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex" data-testid="navbar-actions-desktop">
          <Button
            onClick={toggleLanguage}
            variant="outline"
            className="cursor-pointer whitespace-nowrap border-slate-300"
            data-testid="language-toggle-button"
          >
            <Languages className="h-4 w-4" />
            {isArabic ? "EN" : "AR"}
          </Button>
          <a
            href={`tel:${companyConfig.phone}`}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-primary/90"
            data-testid="navbar-call-button"
          >
            <PhoneCall className="h-4 w-4" />
            {isArabic ? "اتصال مباشر" : "Call Now"}
          </a>
        </div>

        <button
          className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-300 text-slate-700 transition-colors duration-300 hover:bg-slate-100 lg:hidden"
          onClick={() => setMobileOpen((prev) => !prev)}
          data-testid="mobile-menu-toggle-button"
          aria-label="menu toggle"
          type="button"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobileOpen ? (
        <div className="border-t border-slate-200 bg-white px-4 pb-4 lg:hidden" data-testid="mobile-navigation-menu">
          <div className="flex flex-col gap-3 pt-3">
            {content.nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className="rounded-md px-3 py-2 text-sm font-semibold text-slate-700 transition-colors duration-300 hover:bg-slate-100 hover:text-secondary"
                data-testid={`mobile-nav-link-${item.to === "/" ? "home" : item.to.replace("/", "")}`}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={toggleLanguage}
                variant="outline"
                className="flex-1 cursor-pointer whitespace-nowrap"
                data-testid="mobile-language-toggle-button"
              >
                <Languages className="h-4 w-4" /> {language === "ar" ? "EN" : "AR"}
              </Button>
              <a
                href={`tel:${companyConfig.phone}`}
                className="inline-flex flex-1 items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white"
                data-testid="mobile-call-button"
              >
                {isArabic ? "اتصال" : "Call"}
              </a>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
};
